import { useContract } from '@services/contracts/mutations/useContract'
import { useResendSignInvitation } from '@services/contracts/mutations/useResendSignInvitation'
import { useVerifyContractSignature } from '@services/contracts/mutations/useVerifyContractSignature'
import { useContracts } from '@services/contracts/queires/useContracts'
import { Download, Eye, Mail, RotateCw } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { useConfirm } from '@/components/ui/confirm-dialog'
import { cn } from '@/lib/utils'

const TH =
  'px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground'
const TD = 'px-4 py-3.5 align-middle text-sm text-ink'

const formatDate = (value?: string | null) =>
  value ? new Date(value).toLocaleDateString('sk-SK') : '-'

// A row still waiting for its party's signature (same as the backend).
const PENDING_SIGNATURE = ['awaiting', 'sent', 'opened']

function statusStyle(status?: string) {
  if (status === 'completed' || status === 'signed')
    return 'bg-success-soft text-success'
  if (status === 'declined' || status === 'terminated')
    return 'bg-destructive/10 text-destructive'
  return 'bg-peach/15 text-peach'
}

export function ContractTable({
  offerId,
  flatId,
  type = 'offer',
  readOnly = false
}: {
  offerId?: string
  flatId?: string
  type?: 'offer' | 'flat'
  noSearch?: boolean
  /** Hide signature-refresh action (e.g. for the landlord read-only view). */
  readOnly?: boolean
}) {
  const { t } = useTranslation('contract')
  const { mutate: getContract } = useContract()
  const { mutate: verifyContractSignature } = useVerifyContractSignature()
  const resendInvitation = useResendSignInvitation()
  const { confirm, dialog } = useConfirm()
  const query = useContracts({
    params:
      type === 'flat'
        ? { type: 'flat', flat: flatId }
        : { type: 'offer', offer: offerId }
  })
  const contracts = query.data?.results ?? []
  // Flat (owner) contracts have no tenant email or rental period.
  const isOffer = type === 'offer'

  if (!query.isLoading && contracts.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-ink/10 px-4 py-10 text-center text-sm text-muted-foreground">
        —
      </div>
    )
  }

  const StatusPill = ({ status }: { status?: string }) => (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold',
        statusStyle(status)
      )}
    >
      {t(`contract_statuses.${status}`)}
    </span>
  )

  const handleResendInvitation = async (id: string, email?: string) => {
    if (
      await confirm({
        title: t('contract_table.resend_invitation_confirm', { email }),
        description: t('contract_table.resend_invitation_description'),
        confirmText: t('contract_table.resend_invitation'),
        cancelText: t('contract_table.cancel')
      })
    )
      resendInvitation.mutate({ id, type, email })
  }

  const Actions = ({
    id,
    status,
    email
  }: {
    id?: string
    status?: string
    email?: string
  }) => (
    <div className="flex items-center justify-end gap-1.5">
      <Button
        variant="ghostDark"
        size="icon"
        aria-label={t('contract_table.show')}
        onClick={(e) => {
          e.stopPropagation()
          if (id) getContract({ id, params: { action: 'view', type } })
        }}
      >
        <Eye className="h-4 w-4" />
      </Button>
      <Button
        variant="ghostDark"
        size="icon"
        aria-label={t('contract_table.download')}
        onClick={(e) => {
          e.stopPropagation()
          if (id) getContract({ id, params: { action: 'download', type } })
        }}
      >
        <Download className="h-4 w-4" />
      </Button>
      {!readOnly && (
        <Button
          variant="ghostDark"
          size="icon"
          aria-label={t('contract_table.refresh')}
          onClick={(e) => {
            e.stopPropagation()
            if (id) verifyContractSignature({ id, params: { type } })
          }}
        >
          <RotateCw className="h-4 w-4" />
        </Button>
      )}
      {!readOnly && id && PENDING_SIGNATURE.includes(status ?? '') && (
        <Button
          variant="ghostDark"
          size="icon"
          aria-label={t('contract_table.resend_invitation')}
          title={t('contract_table.resend_invitation')}
          disabled={resendInvitation.isPending}
          onClick={(e) => {
            e.stopPropagation()
            void handleResendInvitation(id, email)
          }}
        >
          <Mail className="h-4 w-4" />
        </Button>
      )}
    </div>
  )

  return (
    <>
      {dialog}
      {/* Mobile: stacked rows */}
      <div className="divide-y divide-ink/[0.06] sm:hidden">
        {contracts.map((c) => (
          <div key={c.id} className="space-y-2.5 py-4">
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold text-ink">
                {c.contract_number}
              </span>
              <StatusPill status={c.status} />
            </div>
            <div className="text-sm text-muted-foreground">{c.email}</div>
            <div className="text-sm text-muted-foreground">
              {formatDate(c.start_date)}
              {isOffer && ` – ${formatDate(c.end_date)}`}
            </div>
            <Actions id={c.id} status={c.status} email={c.email} />
          </div>
        ))}
      </div>

      {/* Desktop: table */}
      <div className="hidden overflow-x-auto sm:block">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-ink/[0.08] bg-ink/[0.02] text-left">
              <th className={TH}>{t('contract_table.contract_number')}</th>
              <th className={TH}>{t('contract_table.email')}</th>
              <th className={TH}>{t('contract_table.status')}</th>
              <th className={TH}>{t('contract_table.start_date')}</th>
              {isOffer && (
                <th className={TH}>{t('contract_table.end_date')}</th>
              )}
              <th className={cn(TH, 'text-right')}>
                {t('contract_table.actions')}
              </th>
            </tr>
          </thead>
          <tbody>
            {contracts.map((c) => (
              <tr
                key={c.id}
                className="border-b border-ink/[0.05] transition-colors last:border-0 hover:bg-ink/[0.015]"
              >
                <td className={cn(TD, 'font-medium')}>{c.contract_number}</td>
                <td className={cn(TD, 'text-muted-foreground')}>{c.email}</td>
                <td className={TD}>
                  <StatusPill status={c.status} />
                </td>
                <td className={cn(TD, 'whitespace-nowrap')}>
                  {formatDate(c.start_date)}
                </td>
                {isOffer && (
                  <td className={cn(TD, 'whitespace-nowrap')}>
                    {formatDate(c.end_date)}
                  </td>
                )}
                <td className={cn(TD, 'text-right')}>
                  <Actions id={c.id} status={c.status} email={c.email} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}
